;(() => {
  // Aspect ratio used when the thumbnail isn't loaded yet and we can't read
  // its naturalWidth/Height. 3:2 matches the bulk of property photos.
  const DEFAULT_ASPECT_RATIO = 1.5
  // Final width to fall back to when the data-thumb URL has no `?width=`.
  const DEFAULT_FULL_WIDTH = 1800

  const thumbKey = window.devicePixelRatio > 1.2 ? "thumb-double" : "thumb"

  const imageSizeCache = {}

  let psGallery = null
  let pswpBoundsElement = null // for opening & closing animation

  const disableSwipeNavigationHandler = (e) => {
    const touch = e.touches && e.touches[0]
    if (!touch) return

    // is not near edge of view, exit
    const edgeWidth = 90
    if (touch.pageX > edgeWidth && touch.pageX < window.innerWidth - edgeWidth)
      return

    // don't block taps on PhotoSwipe UI buttons (close, arrows, etc.)
    if (e.target.closest(".pswp__button")) return

    // prevent swipe to navigate back gesture
    e.preventDefault()
  }

  const disableSwipeNavigation = () => {
    psGallery.element.addEventListener(
      "touchstart",
      disableSwipeNavigationHandler,
    )
  }

  const enableSwipeNavigation = () => {
    psGallery.element.removeEventListener(
      "touchstart",
      disableSwipeNavigationHandler,
    )
  }

  // The PhotoSwipe slide is laid out using `data.w` / `data.h` BEFORE the
  // full-size image has loaded. If we pass the thumbnail's pixel size
  // (e.g. 750x500), PhotoSwipe sees a small image and won't upscale it - it
  // renders the slide at 750px wide. Then the real image loads (e.g. 3600px)
  // and PhotoSwipe re-fits to ~1080px wide, producing a visible jump.
  //
  // The fix: predict the natural size of the *full-size* image and seed the
  // slide with it. The full-size URL carries the target width as `?width=N`
  // (Fastly Image Optimization), and the thumbnail's aspect ratio matches
  // the full image (Fastly `fit=bounds` preserves it). So the predicted size
  // is (N, N / thumbAspect), which is exactly what loadComplete will measure.

  const fullWidthFromUrl = (url) => {
    if (typeof url !== "string") return DEFAULT_FULL_WIDTH
    const match = url.match(/[?&]width=(\d+)/)
    return match ? parseInt(match[1], 10) : DEFAULT_FULL_WIDTH
  }

  const predictedFullSize = (src, img) => {
    const w = fullWidthFromUrl(src)
    const aspect =
      img && img.naturalWidth && img.naturalHeight
        ? img.naturalWidth / img.naturalHeight
        : DEFAULT_ASPECT_RATIO
    return { w, h: Math.round(w / aspect) }
  }

  const getData = (el, msrc = null) => {
    const src = el.data(thumbKey)
    const cached = imageSizeCache[src]
    const size = cached || predictedFullSize(src, null)
    const data = {
      src: src,
      title: el.data("title"),
      w: size.w,
      h: size.h,
    }
    if (msrc) {
      data.msrc = msrc
    }
    return data
  }

  const seedDimensions = (photo, img) => {
    if (!img || !img.naturalWidth || !img.naturalHeight) return
    const size = predictedFullSize(photo.src, img)
    photo.w = size.w
    photo.h = size.h
  }

  const getGalleryIndex = ($container, target, size) => {
    const DOMIndex = $container.children().index(target)
    return HB.isLTR() ? DOMIndex : size - 1 - DOMIndex
  }

  const getPhotoByGalleryIndex = ($container, index, size) => {
    const listIndex = HB.isLTR() ? index : size - 1 - index
    return $container.children().get(listIndex)
  }

  const ANIMATION_DURATION = 200

  const showGallery = (photos, index, thumbImg = null) => {
    const rtl = HB.isRTL()

    if (rtl) {
      photos = photos.reverse()
    }
    // Seed dimensions AFTER the reverse: in RTL `index` points at the
    // clicked photo in the reversed array, not in the original DOM order.
    if (thumbImg) {
      seedDimensions(photos[index], thumbImg)
    }

    const options = {
      dataSource: photos,
      index: index,
      showHideOpacity: true,
      showAnimationDuration: ANIMATION_DURATION,
      hideAnimationDuration: ANIMATION_DURATION,
      loop: false,
      maxZoomLevel: 3,
      imageClickAction: "zoom",
      tapAction: "zoom",
      doubleTapAction: "zoom",
      secondaryZoomLevel: "original",
      bgOpacity: 1,
      preload: [1, 3],
      mainClass: photos.length == 1 ? "pswp_one" : undefined,
    }

    psGallery = new PhotoSwipe(options)

    // Thumbnail bounds for open/close animation
    psGallery.addFilter("thumbBounds", (bounds, itemData, instance) => {
      const pageYScroll =
        window.pageYOffset || document.documentElement.scrollTop
      const rect = pswpBoundsElement.getBoundingClientRect()
      return {
        x: rect.left,
        y: rect.top + pageYScroll,
        w: rect.width,
      }
    })

    // Custom caption
    let captionEl = null
    const updateCaption = () => {
      const title = psGallery.currSlide?.data?.title
      if (title) {
        captionEl.innerHTML = title
        captionEl.style.display = ""
      } else {
        captionEl.style.display = "none"
      }
    }

    psGallery.on("uiRegister", () => {
      psGallery.ui.registerElement({
        name: "caption",
        order: 9,
        isButton: false,
        appendTo: "root",
        onInit: (el) => {
          captionEl = el
          el.style.textAlign = "center"
          el.style.padding = "8px 16px"
          el.style.color = "#fff"
          el.style.fontSize = "14px"
          el.style.position = "absolute"
          el.style.bottom = "0"
          el.style.left = "0"
          el.style.width = "100%"
          el.style.background = "rgba(0,0,0,0.3)"
        },
      })
    })

    psGallery.on("change", updateCaption)
    psGallery.on("loadComplete", updateCaption)

    // RTL counter
    if (rtl) {
      let rtlCounterEl = null
      const updateRtlCounter = () => {
        const total = psGallery.getNumItems()
        const current = total - psGallery.currIndex
        rtlCounterEl.dir = "ltr"
        rtlCounterEl.innerHTML = current + " / " + total
      }

      psGallery.on("uiRegister", () => {
        psGallery.ui.registerElement({
          name: "rtl-counter",
          order: 5,
          isButton: false,
          appendTo: "bar",
          onInit: (el) => {
            rtlCounterEl = el
          },
        })
      })

      psGallery.on("change", updateRtlCounter)
      psGallery.on("loadComplete", updateRtlCounter)
    }

    // Update dimensions from actual image once loaded and cache for reuse
    psGallery.on("loadComplete", (e) => {
      const { content, slide } = e
      const img = content.element
      if (!img || !img.naturalWidth) return
      const w = img.naturalWidth
      const h = img.naturalHeight
      if (content.data.w === w && content.data.h === h) return
      content.data.w = w
      content.data.h = h
      imageSizeCache[content.data.src] = { w, h }
      psGallery.refreshSlideContent(slide.index)
    })

    psGallery.init()

    // Control swipe navigation
    disableSwipeNavigation()
    psGallery.on("close", enableSwipeNavigation)
  }

  // Update the closing-animation target as the user swipes between slides,
  // so PhotoSwipe knows which thumbnail to animate the image back to on close.
  const setCorrectClosingTarget = ($container, photos) => {
    psGallery.on("change", () => {
      pswpBoundsElement = getPhotoByGalleryIndex(
        $container,
        psGallery.currIndex,
        photos.length,
      )
    })
  }

  // `galleryId` examples:
  // - property_gallery_data
  // - room_gallery_data_5
  const getPhotos = (galleryId) => {
    const $items = $(`#${galleryId}`).find(".photoswipe--i")
    return $items
      .map(function () {
        return getData($(this))
      })
      .get()
  }

  const closePropertyGalleryOnHashChange = () => {
    if (location.hash !== "#gallery") {
      window.removeEventListener("hashchange", closePropertyGalleryOnHashChange)
      psGallery.close()
    }
  }

  const propertyGalleryCloseHandler = () => {
    if (location.hash === "#gallery") {
      window.removeEventListener("hashchange", closePropertyGalleryOnHashChange)
    }
  }

  const propertyGalleryHandler = function (e) {
    e.preventDefault()
    const photos = getPhotos("property_gallery_data")
    const $container = $(this).closest(".photos")
    const galleryIndex = getGalleryIndex(
      $container,
      e.currentTarget,
      photos.length,
    )
    const $thumbImg = $(this).find(".photos--i--img")
    photos[galleryIndex].msrc = $thumbImg.attr("src")
    pswpBoundsElement = e.currentTarget
    showGallery(photos, galleryIndex, $thumbImg.get(0))

    // Do "Back" after closing gallery or close gallery after "Back"
    psGallery.on("close", propertyGalleryCloseHandler)
    window.addEventListener("hashchange", closePropertyGalleryOnHashChange)

    setCorrectClosingTarget($container, photos)

    return psGallery
  }

  const closeRoomGalleryOnHashChange = () => {
    if (location.hash !== "#room_gallery") {
      window.removeEventListener("hashchange", closeRoomGalleryOnHashChange)
      psGallery.close()
    }
  }

  const roomGalleryCloseHandler = () => {
    if (location.hash === "#room_gallery") {
      window.removeEventListener("hashchange", closeRoomGalleryOnHashChange)
    }
  }

  const roomGalleryInit = (photos, galleryIndex, thumbImg) => {
    showGallery(photos, galleryIndex, thumbImg)

    // Do "Back" after closing gallery or close gallery after "Back"
    psGallery.on("close", roomGalleryCloseHandler)
    window.addEventListener("hashchange", closeRoomGalleryOnHashChange)
  }

  const roomGalleryHandlerMobile = function (
    event,
    roomId,
    galleryIndex = null,
  ) {
    pswpBoundsElement = event.currentTarget
    const photos = getPhotos(`room_gallery_data_${roomId}`)
    let $container
    if (!galleryIndex) {
      $container = $(this).closest(".photos")
      galleryIndex = getGalleryIndex(
        $container,
        event.currentTarget,
        photos.length,
      )
    }
    const thumbImg = $(this).find(".photos--i--img").get(0)
    roomGalleryInit(photos, galleryIndex, thumbImg)

    setCorrectClosingTarget($container, photos)

    return psGallery
  }

  const roomGalleryHandlerDesktop = function (roomId) {
    pswpBoundsElement = this
    const photos = getPhotos(`room_gallery_data_${roomId}`, this)
    const $container = $(this).closest(".room-photos")
    const galleryIndex = getGalleryIndex($container, this, photos.length)
    roomGalleryInit(photos, galleryIndex, this)

    setCorrectClosingTarget($container, photos)

    return psGallery
  }

  window.HB = window.HB || {}
  window.HB.PswpHelpers = {
    ANIMATION_DURATION,
    propertyGalleryHandler,
    roomGalleryHandlerMobile,
    roomGalleryHandlerDesktop,
  }
})()
